import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearch } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { PackageOpen } from 'lucide-react';
import { DashboardSearchInput } from '@/components/dashboard/DashboardSearchInput';
import { ReserviertKnopf } from '@/components/reserviert/ReserviertBlatt';
import { ScanFenster, type ScanModus } from '@/components/scan/ScanFenster';
import { useSuchsprung } from '@/hooks/useSuchsprung';
import { useZeigerFein } from '@/hooks/useLayout';
import { sucheBegonnen } from '@/lib/nutzung';

/**
 * Die Werkzeuge der oberen Leiste am PC (03.11.2026, Skizze A–D,
 * Betreiber: „Suchleiste, Wareneingang usw. am PC immer oben, sogar
 * sticky"): EIN Suchfeld, Wareneingang, Reserviert? — auf jeder Seite.
 *
 * WARUM DAS FELD AUF DEM DASHBOARD LIVE SUCHT UND SONST ERST BEI ENTER
 *   Auf dem Dashboard ist die Liste direkt darunter; jeder Buchstabe darf
 *   sie filtern wie bisher. Auf einer anderen Seite hieße jeder
 *   Buchstabe: Seite verlassen — mitten in einer Bestellung, mitten im
 *   Wort. Dort führt erst Enter weiter, mit demselben Nummern-Sprung wie
 *   auf dem Dashboard (hooks/useSuchsprung.ts).
 *
 * Am Telefon (unter 768 px) gibt es diese Leiste nicht; dort steht das
 * Feld weiter über der Liste.
 */
export function KopfWerkzeuge() {
  const pfad = useLocation({ select: (l) => l.pathname });
  const aufDashboard = pfad === '/';
  const { q } = useSearch({ strict: false }) as { q?: string };
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { absenden, scanVerarbeiten } = useSuchsprung();
  const zeigerFein = useZeigerFein();
  const [wert, setWert] = useState(aufDashboard ? (q ?? '') : '');
  const [scanModus, setScanModus] = useState<ScanModus | null>(null);
  const rahmenRef = useRef<HTMLDivElement>(null);

  // Auf dem Dashboard steht im Feld, was in der Adresse steht — auch wenn
  // ein Chip, „Zurück" oder ein Scan den Begriff ändert. Anderswo bleibt
  // das Feld leer: ein alter Begriff über einer Bestellung sähe aus, als
  // gehöre er zu ihr.
  useEffect(() => {
    setWert(aufDashboard ? (q ?? '') : '');
  }, [aufDashboard, q]);

  // Strg+K (oder ⌘K) und „/" holen den Fokus ins Feld — „/" nur, wenn
  // niemand gerade tippt.
  useEffect(() => {
    function taste(e: KeyboardEvent): void {
      const ziel = e.target as HTMLElement | null;
      const tippt =
        ziel instanceof HTMLElement &&
        (ziel.tagName === 'INPUT' || ziel.tagName === 'TEXTAREA' || ziel.tagName === 'SELECT' || ziel.isContentEditable);
      const strgK = (e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K');
      const schraeg = e.key === '/' && !e.ctrlKey && !e.metaKey && !e.altKey && !tippt;
      if (!strgK && !schraeg) return;
      if (document.querySelector('[role="dialog"], [role="alertdialog"]')) return;
      e.preventDefault();
      rahmenRef.current?.querySelector('input')?.focus();
    }
    window.addEventListener('keydown', taste);
    return () => window.removeEventListener('keydown', taste);
  }, []);

  function aendern(v: string): void {
    setWert(v);
    if (!aufDashboard) return;
    sucheBegonnen(v);
    void navigate({
      to: '/',
      replace: true,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      search: ((prev: any) => {
        const next = { ...prev, q: v };
        if (v === '') delete next.q;
        return next;
      }) as never
    });
  }

  return (
    <div ref={rahmenRef} className="flex min-w-0 flex-1 items-center gap-2" data-testid="kopf-werkzeuge">
      <DashboardSearchInput
        value={wert}
        onChange={aendern}
        onSubmit={() => void absenden(wert)}
        onScanOeffnen={() => setScanModus('suche')}
        tastenHinweis={zeigerFein ? 'Strg K' : undefined}
        platzhalter="Kunde, Nummer, ISBN, Titel …"
      />
      {/* Eigener Einstieg, nicht der Kameraknopf im Suchfeld: Wareneingang
          buchen ist nicht Suchen (E4). Unter 1280 px nur das Symbol, der
          Name steht dann im title und wird vorgelesen. */}
      <button
        data-nutzung="wareneingang"
        type="button"
        data-tour="wareneingang-scan"
        onClick={() => setScanModus('wareneingang')}
        title="Wareneingang — Ware scannen und eintragen"
        className="inline-flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center gap-2 rounded-md border border-line bg-cream px-3 text-sm text-ink-soft hover:border-akzent hover:text-akzent"
      >
        <PackageOpen aria-hidden className="h-4 w-4 shrink-0" />
        <span className="sr-only xl:not-sr-only">Wareneingang</span>
      </button>
      <ReserviertKnopf kompaktUnterXl />
      {scanModus !== null && (
        <ScanFenster
          startModus={scanModus}
          onSchliessen={() => setScanModus(null)}
          onSuchTreffer={(roh) => void scanVerarbeiten(roh)}
          onGebucht={() => void qc.invalidateQueries({ queryKey: ['bestellungen'] })}
        />
      )}
    </div>
  );
}
